import React, { useMemo } from "react";
import SubscriptionFlow from "./SubscriptionFlow";

/**
 * Subscription page entry.
 *
 * Responsibility:
 *  - Parse URL query parameters that drive the flow
 *  - Validate id_project (the only required param) — if missing, show an
 *    inline error rather than booting the flow into a broken state
 *  - Hand parsed values to SubscriptionFlow as plain props
 *
 * URL contract (preserved from legacy):
 *   ?project=15
 *   &plan=42                 (optional; user can change in step 2)
 *   &promo_code=PROMO2024    (optional; pre-applied in sidebar)
 *   &ref=REF12345            (optional)
 *   &lang=th                 (optional; default "th")
 *
 * Why useMemo for parsing:
 *  We don't want to re-parse on every render, and we don't want React to
 *  treat the parsed object as "new" and trigger child re-renders.
 *
 * Why this is a separate file from SubscriptionFlow:
 *  Keeps URL handling / param validation OUT of the orchestrator — that's
 *  router-layer concern. SubscriptionFlow now takes plain props and is
 *  trivially testable (just render with any props you want).
 */
function SubscriptionPage() {
  const params = useMemo(() => parseUrlParams(), []);

  if (!params.id_project) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center text-center p-4" style={{ height: "100vh" }}>
        <h5 className="fw-bold mb-2">ลิงก์ไม่ถูกต้อง</h5>
        <p className="text-muted">
          ไม่พบรหัสโปรเจกต์ในลิงก์ — กรุณากลับไปยังหน้าโปรโมชันแล้วคลิกลิงก์อีกครั้ง
        </p>
      </div>
    );
  }

  return (
    <SubscriptionFlow
      id_project={params.id_project}
      initialPlanId={params.plan}
      initialPromo={params.promo_code}
      initialRef={params.ref}
      lang={params.lang}
    />
  );
}

function parseUrlParams() {
  // window.location.search is the canonical source. URLSearchParams handles
  // edge cases (encoded chars, repeated keys) better than manual splitting.
  const sp = new URLSearchParams(window.location.search);

  // Sanity-check id_project: must be a positive integer
  const rawProject = sp.get("project");
  const id_project = /^\d+$/.test(rawProject ?? "") ? Number(rawProject) : null;

  // plan: same — but null is OK (orchestrator falls back to first plan)
  const rawPlan = sp.get("plan");
  const plan = /^\d+$/.test(rawPlan ?? "") ? Number(rawPlan) : null;

  // codes: trim + uppercase since DB lookup normalizes them anyway
  const promo_code = (sp.get("promo_code") ?? "").trim().toUpperCase();
  const ref = (sp.get("ref") ?? "").trim().toUpperCase();

  // lang: only accept known values
  const langRaw = sp.get("lang");
  const lang = langRaw === "en" ? "en" : "th";

  return { id_project, plan, promo_code, ref, lang };
}

export default SubscriptionPage;